/**
 * What the capture tray of the menu source screen shows (plan §4.3): the
 * pages this device is still preparing, uploading or failed to upload, plus
 * the session's uploaded pages that were not read yet, one tile per page the
 * owner added (the parts of a very tall page are one tile), and the «Leer mi
 * carta» button built from them. Pure.
 */
import type { ICaptureEntry } from '@/composition/menu-import/use-menu-capture';
import type {
  IMenuImportPage,
  TMenuImportPageOrigin,
  TMenuImportPageWarning,
} from '@/interfaces/IMenuImport';
import { PAGES_PER_READ } from './capture-files';
import { groupOwnerPages } from './owner-pages';
import type { IPartIdentity } from './owner-pages';

export type TTrayTileKind = 'photo' | 'pdf' | 'sheet' | 'text';
export type TTrayTileState = 'processing' | 'uploading' | 'ready' | 'failed';

/** One image of a tray page: this device's capture entry and/or the session page. */
export interface ITrayMember {
  localId: string | null;
  pageId: string | null;
}

/** One thumbnail of the tray: a page as the owner added it. */
export interface ITrayTile {
  /** Its first image's `clientPageId`: stable from the first local step to the uploaded page. */
  key: string;
  /** Its images in order: one, or the parts of a very tall page. */
  members: ITrayMember[];
  kind: TTrayTileKind;
  /** Page number inside the PDF. */
  originPage: number | null;
  /** The parts of a very tall page read in parts (so far, while it is cut); null for a whole page. */
  parts: number | null;
  /** A very tall page cut short on this device: only its beginning is read. */
  cut: boolean;
  /** Object URL of its first processed JPEG on this device. */
  previewUrl: string | null;
  /** Where its first uploaded JPEG is stored (image pages). */
  storageKey: string | null;
  /** Failed when any image failed, else preparing, else uploading, else ready. */
  state: TTrayTileState;
  /** The photo check still shown (a whole photo); null when none or once «Usar igual» hid it. */
  warning: TMenuImportPageWarning | null;
  /** i18n key of its first upload failure. */
  errorKey: string | null;
  /** Upload progress of all its images, 0..1. */
  progress: number;
}

/** One image of the tray, before the parts of a page are grouped. */
interface ITrayImage extends IPartIdentity {
  key: string;
  localId: string | null;
  pageId: string | null;
  kind: TTrayTileKind;
  previewUrl: string | null;
  storageKey: string | null;
  state: TTrayTileState;
  cut: boolean;
  warning: TMenuImportPageWarning | null;
  errorKey: string | null;
  progress: number;
}

/** An uploaded page that no read took yet. */
export function isUnreadPage(page: IMenuImportPage): boolean {
  return page.status === 'uploaded' && page.readNo === null;
}

function pageKind(origin: TMenuImportPageOrigin): TTrayTileKind {
  switch (origin) {
    case 'pdf':
      return 'pdf';
    case 'sheet':
      return 'sheet';
    case 'paste':
      return 'text';
    default:
      return 'photo';
  }
}

function entryKind(kind: ICaptureEntry['kind']): TTrayTileKind {
  return kind === 'paste' ? 'text' : kind;
}

/** A page still being cut in parts is still being prepared, whatever its first part's upload. */
function entryState(entry: ICaptureEntry): TTrayTileState {
  return entry.cutting && entry.state !== 'failed' ? 'processing' : entry.state;
}

function entryWarning(entry: ICaptureEntry): TMenuImportPageWarning | null {
  return entry.warningDismissed || entry.warning === 'none'
    ? null
    : entry.warning;
}

/**
 * The session's unread images in `position` order (with this device's
 * preview and warning when it uploaded them), then this device's images that
 * are not in that list yet (being prepared or uploaded, failed, or uploaded
 * after the last snapshot) in the order they were added. Images already
 * taken by a read are left out.
 */
function trayImages(
  entries: readonly ICaptureEntry[],
  pages: readonly IMenuImportPage[]
): ITrayImage[] {
  const entryByClientPageId = new Map(
    entries.map((entry) => [entry.clientPageId, entry])
  );
  const knownPageIds = new Set(pages.map((page) => page.pageId));
  const images: ITrayImage[] = pages
    .filter(isUnreadPage)
    .sort((a, b) => a.position - b.position)
    .map((page) => {
      const entry = entryByClientPageId.get(page.clientPageId) ?? null;
      return {
        key: page.clientPageId,
        localId: entry?.localId ?? null,
        pageId: page.pageId,
        kind: pageKind(page.origin),
        origin: page.origin,
        originName: page.originName,
        originPage: page.originPage,
        originPart: page.originPart,
        previewUrl: entry?.previewUrl ?? null,
        storageKey: page.storageKey,
        state: entry ? entryState(entry) : 'ready',
        cut: entry?.cut ?? false,
        warning: entry ? entryWarning(entry) : page.clientWarning,
        errorKey: null,
        progress: 1,
      };
    });
  const shown = new Set(images.map((image) => image.key));
  for (const entry of entries) {
    if (shown.has(entry.clientPageId)) continue;
    // Listed but no longer unread: a read took it.
    if (entry.pageId !== null && knownPageIds.has(entry.pageId)) continue;
    images.push({
      key: entry.clientPageId,
      localId: entry.localId,
      pageId: entry.pageId,
      kind: entryKind(entry.kind),
      origin: entry.origin,
      originName: entry.originName,
      originPage: entry.originPage,
      originPart: entry.originPart,
      previewUrl: entry.previewUrl,
      storageKey: null,
      state: entryState(entry),
      cut: entry.cut,
      warning: entry.state === 'ready' ? entryWarning(entry) : null,
      errorKey: entry.errorKey,
      progress: entry.progress,
    });
  }
  return images;
}

/** A page's state from its images: a failure first, then what is still preparing, then uploading. */
function pageState(images: readonly ITrayImage[]): TTrayTileState {
  for (const state of ['failed', 'processing', 'uploading'] as const) {
    if (images.some((image) => image.state === state)) return state;
  }
  return 'ready';
}

/** The tray's pages, one tile per page the owner added (see `trayImages` for the order). */
export function trayTiles(
  entries: readonly ICaptureEntry[],
  pages: readonly IMenuImportPage[]
): ITrayTile[] {
  return groupOwnerPages(trayImages(entries, pages)).map(
    (images): ITrayTile => {
      const [first] = images;
      const failed = images.find((image) => image.state === 'failed');
      return {
        key: first.key,
        members: images.map((image) => ({
          localId: image.localId,
          pageId: image.pageId,
        })),
        kind: first.kind,
        originPage: first.originPage,
        parts: first.originPart === null ? null : images.length,
        cut: first.cut,
        previewUrl: first.previewUrl,
        storageKey: first.storageKey,
        state: pageState(images),
        warning: images.length === 1 ? first.warning : null,
        errorKey: failed?.errorKey ?? null,
        progress:
          images.reduce((sum, image) => sum + image.progress, 0) /
          images.length,
      };
    }
  );
}

export type TReadCtaKey =
  | 'menuImport.read.cta'
  | 'menuImport.read.ctaPhotos'
  | 'menuImport.read.ctaPages'
  | 'menuImport.read.ctaUploading';

/** The «Leer mi carta» button of the source screen. */
export interface IReadCta {
  labelKey: TReadCtaKey;
  count: number;
  /** Rendered with `$tc` (the photos/pages labels have two forms). */
  plural: boolean;
  /** At least one unread page and nothing still being prepared or uploaded. */
  ready: boolean;
  /** More images than the pages of a read (the parts of very tall pages): it takes longer. */
  long: boolean;
}

/**
 * «Leer mi carta ({count} fotos)» when every unread page is a photo, «…
 * ({count} páginas)» when any is a PDF page, a spreadsheet or text (the parts
 * of a long page count once), «Subiendo {count}…» while pages are still being
 * prepared or uploaded, and a plain «Leer mi carta» (disabled) before the
 * first page: no «(0 fotos)».
 *
 * @param uploading pages of the tray being prepared or uploaded.
 * @param busy anything still being prepared (a PDF being rendered counts before its pages exist).
 */
export function readCta(
  pages: readonly IMenuImportPage[],
  uploading: number,
  busy: boolean
): IReadCta {
  const unread = pages
    .filter(isUnreadPage)
    .sort((a, b) => a.position - b.position);
  const long = unread.length > PAGES_PER_READ;
  if (uploading > 0)
    return {
      labelKey: 'menuImport.read.ctaUploading',
      count: uploading,
      plural: false,
      ready: false,
      long,
    };
  const count = groupOwnerPages(unread).length;
  if (unread.length === 0)
    return {
      labelKey: 'menuImport.read.cta',
      count: 0,
      plural: false,
      ready: false,
      long,
    };
  const onlyPhotos = unread.every(
    (page) => page.origin === 'camera' || page.origin === 'gallery'
  );
  return {
    labelKey: onlyPhotos
      ? 'menuImport.read.ctaPhotos'
      : 'menuImport.read.ctaPages',
    count,
    plural: true,
    ready: unread.length > 0 && !busy,
    long,
  };
}
